import { RadioButtonGroupData, SelectData } from "../../models";

export class TaskConstants {
  // Per-record ACL permission granting read access to a task, used to resolve its visibility.
  public static readonly PERMISSION_READ_WORKFLOW_TASK = "readWorkflowTask";

  // Type — FE-only distinction, derived from workflow step nullability
  public static readonly TASK_TYPE_WORKFLOW = "Workflow";

  public static readonly TASK_TYPE_GENERAL = "General";

  public static readonly TASK_TYPE_RADIO_DATA = [
    { key: TaskConstants.TASK_TYPE_WORKFLOW, value: TaskConstants.TASK_TYPE_WORKFLOW },
    { key: TaskConstants.TASK_TYPE_GENERAL, value: TaskConstants.TASK_TYPE_GENERAL },
  ] as RadioButtonGroupData;

  public static readonly TASK_TYPE_TOOLTIP_TEXT =
    "A workflow task belongs to a phase and step of the project workflow. A general task is standalone and is not tied to a workflow step.";

  // Status
  public static readonly TASK_STATUS_COMPLETE = "Complete";

  public static readonly TASK_STATUS_TO_DO = "To do";

  public static readonly TASK_STATUS_UNASSIGNED = "Unassigned";

  public static readonly TASK_STATUS_IN_PROGRESS = "In progress";

  public static readonly TASK_STATUS_IN_REVIEW = "In review";

  public static readonly TASK_STATUS_BLOCKED = "Blocked";

  // Resolution
  public static readonly TASK_RESOLUTION_FINISHED = "Finished";

  public static readonly TASK_RESOLUTION_WONT_DO = "Won't do";

  public static readonly TASK_RESOLUTION_DUPLICATE = "Duplicate";

  // Reminder types accepted by the backend (CreateWorkflowTaskValidator: "Email" | "App").
  // The overlay has no reminder-type selector, so a fixed default is sent when a reminder is enabled.
  public static readonly TASK_REMINDER_TYPE_DEFAULT = "Email";

  public static readonly DEFAULT_TASK = {
    deadlineDate: null,
    isMandatory: null,
    isMilestone: null,
    status: null,
    keyDate: null,
    repeat: null,
    description: null,
    name: null,
    visibility: null,
    assignedToUserUuid: null,
    assignedToUserName: null,
    ownerOrganisationUuid: null,
    relateToActivity: false,
    activityUuid: null,
    objectType: null,
    objectUuid: null,
    stepUuid: null,
    phase: null,
    uuid: null,
    rowVersion: null,
    resolution: null,
    reminderDate: null,
    reminderType: null,
    addReminder: false,
  };

  public static readonly STATUS_SELECT_DATA = [
    { key: TaskConstants.TASK_STATUS_UNASSIGNED, value: TaskConstants.TASK_STATUS_UNASSIGNED },
    { key: TaskConstants.TASK_STATUS_TO_DO, value: TaskConstants.TASK_STATUS_TO_DO },
    { key: TaskConstants.TASK_STATUS_IN_PROGRESS, value: TaskConstants.TASK_STATUS_IN_PROGRESS },
    { key: TaskConstants.TASK_STATUS_IN_REVIEW, value: TaskConstants.TASK_STATUS_IN_REVIEW },
    { key: TaskConstants.TASK_STATUS_COMPLETE, value: TaskConstants.TASK_STATUS_COMPLETE },
    { key: TaskConstants.TASK_STATUS_BLOCKED, value: TaskConstants.TASK_STATUS_BLOCKED },
  ] as SelectData;

  public static readonly ASSIGNED_TASK_STATUS_SELECT_DATA = [
    { key: TaskConstants.TASK_STATUS_TO_DO, value: TaskConstants.TASK_STATUS_TO_DO },
    { key: TaskConstants.TASK_STATUS_IN_PROGRESS, value: TaskConstants.TASK_STATUS_IN_PROGRESS },
    { key: TaskConstants.TASK_STATUS_IN_REVIEW, value: TaskConstants.TASK_STATUS_IN_REVIEW },
    { key: TaskConstants.TASK_STATUS_COMPLETE, value: TaskConstants.TASK_STATUS_COMPLETE },
    { key: TaskConstants.TASK_STATUS_BLOCKED, value: TaskConstants.TASK_STATUS_BLOCKED },
  ];

  public static readonly RESOLUTION_SELECT_DATA = [
    { key: TaskConstants.TASK_RESOLUTION_FINISHED, value: TaskConstants.TASK_RESOLUTION_FINISHED },
    { key: TaskConstants.TASK_RESOLUTION_WONT_DO, value: TaskConstants.TASK_RESOLUTION_WONT_DO },
    { key: TaskConstants.TASK_RESOLUTION_DUPLICATE, value: TaskConstants.TASK_RESOLUTION_DUPLICATE },
  ];
}

/*
  The object_type values that name a task on an API record, normalised. The spelling differs by feature —
  InteractionObjectType.WORKFLOW_TASK is "WorkflowTask" while DiscussionObjectTypeConstants.WORKFLOW_TASK is
  "Workflow Task" — so comparing against either constant alone silently misses records carrying the other.
*/
const TASK_OBJECT_TYPES = new Set(["task", "workflowtask", "generaltask"]);

/*
  True when an API record's object_type names a task, whichever spelling it arrives in: spacing, hyphens,
  underscores and case are all normalised away first. An unrecognised type reads as "not a task" rather
  than throwing, so a caller gates behaviour on it safely as new object types appear on the API.
*/
export const isTaskObjectType = (objectType: string | null | undefined): boolean =>
  TASK_OBJECT_TYPES.has((objectType ?? "").replace(/[\s_-]/g, "").toLowerCase());
